import React, { useState, useMemo } from "react";
import { Typography, Tag, Divider, Button, message } from "antd";
import { CopyOutlined, CheckOutlined } from "@ant-design/icons";
import {
  getAllJobPostsDocs,
  getJobPostByIdDocs,
  getAllJobApplicationsDocs,
  getJobApplicationByIdDocs,
  getAllCandidatesDocs,
  getCandidateByIdDocs,
  loginDocs,
} from "./docs";
const { Title, Text, Paragraph } = Typography;

interface DocContentProps {
  activeItem: string;
}
const DocContent: React.FC<DocContentProps> = ({ activeItem }) => {
  const [copiedRequest, setCopiedRequest] = useState(false);
  const [copiedResponse, setCopiedResponse] = useState(false);

  const handleCopy = async (text: string, type: "request" | "response") => {
    if (navigator.clipboard) {
      try {
        await navigator.clipboard.writeText(text);
        message.success("Copied to clipboard");
        if (type === "request") {
          setCopiedRequest(true);
          setTimeout(() => setCopiedRequest(false), 2000);
        } else {
          setCopiedResponse(true);
          setTimeout(() => setCopiedResponse(false), 2000);
        }
      } catch (err) {
        message.error("Failed to copy");
        console.error("Failed to copy: ", err);
      }
    } else {
      // Fallback for browsers that don't support clipboard API
      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.top = "0";
      textArea.style.left = "0";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      try {
        document.execCommand("copy");
        message.success("Copied to clipboard");
        if (type === "request") {
          setCopiedRequest(true);
          setTimeout(() => setCopiedRequest(false), 2000);
        } else {
          setCopiedResponse(true);
          setTimeout(() => setCopiedResponse(false), 2000);
        }
      } catch (err) {
        message.error("Failed to copy");
        console.error("Fallback: Oops, unable to copy", err);
      }

      document.body.removeChild(textArea);
    }
  };

  const doc = useMemo(() => {
    switch (activeItem) {
      case "Retrieve all job posts":
        return getAllJobPostsDocs;
      case "Retrieve a job post by id":
        return getJobPostByIdDocs;
      case "Retrieve all job applications":
        return getAllJobApplicationsDocs;
      case "Retrieve a job application by id":
        return getJobApplicationByIdDocs;
      case "Retrieve all candidates":
        return getAllCandidatesDocs;
      case "Retrieve a candidate by id":
        return getCandidateByIdDocs;
      case "Login":
        return loginDocs;
      default:
        return getAllJobPostsDocs;
    }
  }, [activeItem]);

  return (
    <div className="doc-content-wrapper">
      <main className="doc-content-main">
        <div className="content-wrapper">
          <Title level={3} className="endpoint-title" style={{ margin: 0 }}>
            {doc?.title}
          </Title>
          <div className="endpoint-method">
            <Tag color="blue">{doc?.method}</Tag>
            <Text code className="endpoint-path">
              {doc?.path}
            </Text>
          </div>
          <Paragraph className="endpoint-description">
            {doc?.description}
          </Paragraph>

          <Divider style={{ margin: "0" }} />

          <div className="endpoint-attributes">
            {doc && doc?.requiredAttributes.length > 0 && (
              <Title level={5} style={{ margin: 0 }}>
                Required attributes
              </Title>
            )}

            {doc?.requiredAttributes.map((attribute) => (
              <div className="attribute-item">
                <div className="attribute-header">
                  <Text strong>{attribute.name}</Text>
                  <Tag>{attribute.type}</Tag>
                </div>
                <Paragraph type="secondary" className="attribute-description">
                  {attribute.description}
                </Paragraph>
              </div>
            ))}

            {doc && doc?.optionalAttributes.length > 0 && (
              <Title
                level={5}
                style={{ margin: 0 }}
                className="optional-attributes-title"
              >
                Optional attributes
              </Title>
            )}
            {doc?.optionalAttributes.map((attribute) => (
              <div className="attribute-item">
                <div className="attribute-header">
                  <Text strong>{attribute.name}</Text>
                  <Tag>{attribute.type}</Tag>
                </div>
                <Paragraph type="secondary" className="attribute-description">
                  {attribute.description}
                </Paragraph>
              </div>
            ))}
          </div>
        </div>
      </main>
      {/* Right Panel - Code Examples */}
      <aside className="api-docs-code-panel">
        <div className="code-panel-content">
          <div className="code-section">
            <Title
              style={{ margin: 0 }}
              level={5}
              className="code-section-title"
            >
              Request
            </Title>
            <div className="code-block-wrapper">
              <Button
                type="text"
                icon={copiedRequest ? <CheckOutlined /> : <CopyOutlined />}
                onClick={() => handleCopy(doc?.request || "", "request")}
                className="code-copy-button"
              />
              <pre className="code-block">
                <code>{doc?.request}</code>
              </pre>
            </div>
          </div>

          <div className="code-section">
            <Title
              level={5}
              className="code-section-title"
              style={{ margin: 0 }}
            >
              Response
            </Title>
            <div className="code-block-wrapper">
              <Button
                type="text"
                icon={copiedResponse ? <CheckOutlined /> : <CopyOutlined />}
                onClick={() => handleCopy(doc?.response || "", "response")}
                className="code-copy-button"
              />
              <pre className="code-block">
                <code>{doc?.response || ""}</code>
              </pre>
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
};

export default DocContent;
